import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { showsSoupAttribution } from "../utils/planFeatures";

/**
 * The "Reservas com Soup" mark shown on the pages diners see: the embedded
 * widget, the public booking link and the customer's own reservation page.
 *
 * Deliberately not the Footer component. This sits inside someone else's
 * website, so it is one line of text and a link - no contacts, no socials, no
 * copyright. It has to read as infrastructure, not as an advert.
 *
 * Visibility is decided by the plan, not by the caller: free restaurants always
 * carry it, paid ones may switch it off in settings. Render it unconditionally
 * and let showsSoupAttribution decide.
 */

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 5px;
  padding: 10px 12px 14px;
  font-size: 11px;
  line-height: 1.4;
`;

const Label = styled.span`
  color: #99b0ad;
  letter-spacing: 0.02em;
`;

const Mark = styled.a`
  color: #ee754c;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

// Where the click came from, so the visits this earns can be told apart from
// the rest of the traffic in analytics.
const SOURCES = {
  widget: "widget",
  booking: "booking_link",
  reservation: "guest_page"
};

const SoupAttribution = ({ restaurant, source = "widget", className }) => {
  const { t } = useTranslation();

  if (!showsSoupAttribution(restaurant)) return null;

  const utmSource = SOURCES[source] || SOURCES.widget;
  const href = `https://www.soup.red/?utm_source=${utmSource}&utm_medium=attribution&utm_campaign=powered_by`;

  return (
    <Wrapper className={className}>
      <Label>{t("ui.components.soupAttribution.reservasCom")}</Label>
      <Mark href={href} target="_blank" rel="noopener noreferrer">
        Soup
      </Mark>
    </Wrapper>
  );
};

export default SoupAttribution;
